import React from "react";
import {
  Shuffle, Swords, Users, Compass, Gem, AlertTriangle, Sparkles,
  ShoppingBag, Coffee, Radio, Globe, BookOpen, Megaphone, Flag,
  Zap, Clock, ArrowRight, CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

interface EncounterEntry {
  type: string;
  outcome: string;
  turn: number;
  description?: string;
  rewards?: string;
  affects_plot?: boolean;
}

interface NewsEntry {
  headline: string;
  details?: string;
  channel: string;
  source_name?: string;
  reliability?: string;
  turn: number;
  is_relevant_to_player?: boolean;
  could_be_quest_hook?: boolean;
}

interface WorldEvent {
  title: string;
  description: string;
  category: string;
  severity?: string;
  duration?: string;
  turn_created: number;
  resolved?: boolean;
  player_can_intervene?: boolean;
}

interface EventRipple {
  source_event: string;
  description: string;
  timing: "immediate" | "near" | "far";
  affects?: string[];
  category?: string;
  created_turn: number;
  injected: boolean;
  injected_turn?: number;
  narrative_note?: string;
}

interface EncounterLogWidgetProps {
  encounter_history?: EncounterEntry[];
  world_news_log?: NewsEntry[];
  active_world_events?: WorldEvent[];
  event_ripples?: EventRipple[];
  turns_since_encounter?: number;
  beats_since_encounter?: number;
  turns_since_news?: number;
  total_encounters?: number;
  total_events?: number;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  combat: { icon: <Swords className="h-3 w-3" />, color: "text-red-500" },
  social: { icon: <Users className="h-3 w-3" />, color: "text-blue-400" },
  exploration: { icon: <Compass className="h-3 w-3" />, color: "text-emerald-500" },
  treasure: { icon: <Gem className="h-3 w-3" />, color: "text-amber-400" },
  hazard: { icon: <AlertTriangle className="h-3 w-3" />, color: "text-orange-500" },
  event: { icon: <Sparkles className="h-3 w-3" />, color: "text-purple-400" },
  world_event: { icon: <Globe className="h-3 w-3" />, color: "text-purple-400" },
  news: { icon: <Radio className="h-3 w-3" />, color: "text-sky-400" },
  story_beat: { icon: <BookOpen className="h-3 w-3" />, color: "text-rose-400" },
  merchant: { icon: <ShoppingBag className="h-3 w-3" />, color: "text-teal-400" },
  rest: { icon: <Coffee className="h-3 w-3" />, color: "text-green-400" },
  ambient: { icon: <Sparkles className="h-3 w-3" />, color: "text-indigo-400" },
  faction: { icon: <Flag className="h-3 w-3" />, color: "text-orange-400" },
};

const OUTCOME_STYLES: Record<string, string> = {
  success: "bg-emerald-500/15 text-emerald-600",
  partial: "bg-amber-500/15 text-amber-600",
  failure: "bg-red-500/15 text-red-600",
  fled: "bg-muted text-muted-foreground",
  ignored: "bg-muted text-muted-foreground",
};

const SEVERITY_STYLES: Record<string, string> = {
  minor: "bg-muted text-muted-foreground",
  moderate: "bg-amber-500/15 text-amber-600",
  major: "bg-orange-500/15 text-orange-600",
  catastrophic: "bg-red-500/15 text-red-600",
};

const RELIABILITY_STYLES: Record<string, string> = {
  confirmed: "text-emerald-500",
  likely: "text-blue-400",
  rumor: "text-amber-500",
  propaganda: "text-orange-500",
  misinformation: "text-red-500",
};

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  radio: <Radio className="h-3 w-3" />,
  tavern_gossip: <Users className="h-3 w-3" />,
  town_crier: <Megaphone className="h-3 w-3" />,
  newspaper: <BookOpen className="h-3 w-3" />,
  data_feed: <Globe className="h-3 w-3" />,
  messenger: <Users className="h-3 w-3" />,
  overheard: <Users className="h-3 w-3" />,
  posted_notice: <BookOpen className="h-3 w-3" />,
  npc_conversation: <Users className="h-3 w-3" />,
};

const TIMING_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  immediate: { icon: <Zap className="h-3 w-3" />, label: "Now", color: "text-amber-500" },
  near: { icon: <Clock className="h-3 w-3" />, label: "Soon", color: "text-blue-400" },
  far: { icon: <ArrowRight className="h-3 w-3" />, label: "Later", color: "text-purple-400" },
};

export function EncounterLogWidget({
  encounter_history = [],
  world_news_log = [],
  active_world_events = [],
  event_ripples = [],
  turns_since_encounter = 0,
  beats_since_encounter = 0,
  turns_since_news = 0,
  total_encounters = 0,
  total_events = 0,
}: EncounterLogWidgetProps) {
  const { t } = useLocale();
  const pendingRipples = event_ripples.filter(r => !r.injected);
  const injectedRipples = event_ripples.filter(r => r.injected);
  const hasContent = encounter_history.length > 0 || world_news_log.length > 0 || active_world_events.length > 0 || event_ripples.length > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Shuffle className="h-8 w-8 text-muted-foreground/50" />
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t.play_no_events}</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-[200px]">
            {t.play_no_events_desc}
          </p>
        </div>
      </div>
    );
  }

  const activeEvents = active_world_events.filter(e => !e.resolved);

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Shuffle className="h-4 w-4 text-primary" />
        <span>{t.play_events_world}</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">
          {total_encounters + total_events}
        </Badge>
      </div>

      {/* Active World Events Banner */}
      {activeEvents.length > 0 && (
        <div className="space-y-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_active_events}</span>
          {activeEvents.map((evt, i) => {
            const cfg = TYPE_CONFIG[evt.category] || TYPE_CONFIG.world_event;
            return (
              <div key={i} className="rounded-md border border-primary/20 bg-primary/5 p-2 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span className={cfg.color}>{cfg.icon}</span>
                  <span className="text-xs font-medium text-foreground flex-1">{evt.title}</span>
                  {evt.severity && (
                    <Badge className={cn("text-[8px] px-1 py-0 border-0", SEVERITY_STYLES[evt.severity] || SEVERITY_STYLES.moderate)}>
                      {evt.severity}
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">{evt.description}</p>
                <div className="flex gap-2 text-[9px] text-muted-foreground/60">
                  {evt.duration && <span>⏱ {evt.duration}</span>}
                  {evt.player_can_intervene && <span className="text-primary">⚡ Actionable</span>}
                  <span>Turn {evt.turn_created}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pending Ripples */}
      {pendingRipples.length > 0 && (
        <div className="space-y-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">🔮 {t.play_pending_ripples}</span>
          {pendingRipples.map((ripple, i) => {
            const tcfg = TIMING_CONFIG[ripple.timing] || TIMING_CONFIG.near;
            return (
              <div key={i} className="rounded-md border border-border/50 bg-muted/30 p-1.5 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span className={tcfg.color}>{tcfg.icon}</span>
                  <span className="text-[11px] text-foreground flex-1">{ripple.description}</span>
                  <Badge variant="outline" className={cn("text-[8px] px-1 py-0 border-0", tcfg.color)}>
                    {tcfg.label}
                  </Badge>
                </div>
                <div className="flex gap-2 text-[9px] text-muted-foreground/60">
                  <span>from: {ripple.source_event}</span>
                  {ripple.affects?.length ? <span>→ {ripple.affects.join(", ")}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="h-7 w-full">
          <TabsTrigger value="all" className="text-[10px] h-5 flex-1">{t.play_enc_all}</TabsTrigger>
          <TabsTrigger value="encounters" className="text-[10px] h-5 flex-1">
            {t.play_enc_encounters} {encounter_history.length > 0 && <span className="ml-1 text-[8px]">({encounter_history.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="news" className="text-[10px] h-5 flex-1">
            {t.play_enc_news} {world_news_log.length > 0 && <span className="ml-1 text-[8px]">({world_news_log.length})</span>}
          </TabsTrigger>
          {injectedRipples.length > 0 && (
            <TabsTrigger value="ripples" className="text-[10px] h-5 flex-1">
              {t.play_enc_ripples} <span className="ml-1 text-[8px]">({injectedRipples.length})</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="mt-1.5">
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-1.5">
              <div className="flex gap-4 text-[10px] text-muted-foreground mb-1">
                <span>{beats_since_encounter || turns_since_encounter} {t.play_enc_since_event}</span>
                {world_news_log.length > 0 && (
                  <span>{turns_since_news} {t.play_enc_since_news}</span>
                )}
              </div>
              {[
                ...encounter_history.map(e => ({ ...e, _kind: "encounter" as const })),
                ...world_news_log.map(n => ({ ...n, _kind: "news" as const, type: "news" })),
              ]
                .sort((a, b) => (b.turn || 0) - (a.turn || 0))
                .map((item, i) => {
                  if (item._kind === "news") {
                    const news = item as NewsEntry & { _kind: "news" };
                    return <NewsCard key={`n-${i}`} news={news} />;
                  }
                  const enc = item as EncounterEntry & { _kind: "encounter" };
                  return <EncounterCard key={`e-${i}`} enc={enc} />;
                })}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="encounters" className="mt-1.5">
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-1.5">
              {encounter_history.length === 0 ? (
                <p className="text-[11px] text-muted-foreground text-center py-4">{t.play_enc_no_encounters}</p>
              ) : (
                [...encounter_history].reverse().map((enc, i) => (
                  <EncounterCard key={i} enc={enc} />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="news" className="mt-1.5">
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-1.5">
              {world_news_log.length === 0 ? (
                <p className="text-[11px] text-muted-foreground text-center py-4">{t.play_enc_no_news}</p>
              ) : (
                [...world_news_log].reverse().map((news, i) => (
                  <NewsCard key={i} news={news} />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {injectedRipples.length > 0 && (
          <TabsContent value="ripples" className="mt-1.5">
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-1.5">
                {injectedRipples.map((ripple, i) => (
                  <div key={i} className="rounded-md border border-border/50 bg-muted/20 p-2 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      <span className="text-[11px] text-foreground flex-1">{ripple.description}</span>
                    </div>
                    <div className="flex gap-2 text-[9px] text-muted-foreground/60">
                      <span>from: {ripple.source_event}</span>
                      {ripple.injected_turn != null && <span>woven at turn {ripple.injected_turn}</span>}
                    </div>
                    {ripple.narrative_note && (
                      <p className="text-[10px] text-muted-foreground italic">{ripple.narrative_note}</p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function EncounterCard({ enc }: { enc: EncounterEntry }) {
  const cfg = TYPE_CONFIG[enc.type] || { icon: <Sparkles className="h-3 w-3" />, color: "text-muted-foreground" };
  return (
    <div className="rounded-md border border-border p-2 space-y-1">
      <div className="flex items-center gap-1.5">
        <span className={cfg.color}>{cfg.icon}</span>
        <span className="text-xs font-medium text-foreground capitalize">{enc.type.replace(/_/g, " ")}</span>
        {enc.affects_plot && (
          <BookOpen className="h-2.5 w-2.5 text-rose-400 ml-0.5" />
        )}
        <Badge className={cn("ml-auto text-[9px] px-1 py-0 border-0", OUTCOME_STYLES[enc.outcome] || OUTCOME_STYLES.partial)}>
          {enc.outcome}
        </Badge>
      </div>
      {enc.description && (
        <p className="text-[11px] text-muted-foreground">{enc.description}</p>
      )}
      {enc.rewards && (
        <p className="text-[10px] text-amber-500">🏆 {enc.rewards}</p>
      )}
      <p className="text-[9px] text-muted-foreground/50">Turn {enc.turn}</p>
    </div>
  );
}

function NewsCard({ news }: { news: NewsEntry }) {
  const channelIcon = CHANNEL_ICONS[news.channel] || <Radio className="h-3 w-3" />;
  return (
    <div className={cn(
      "rounded-md border p-2 space-y-1",
      news.is_relevant_to_player ? "border-primary/30 bg-primary/5" : "border-border",
    )}>
      <div className="flex items-center gap-1.5">
        <span className="text-sky-400">{channelIcon}</span>
        <span className="text-xs font-medium text-foreground flex-1">{news.headline}</span>
        {news.could_be_quest_hook && (
          <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-500 border-amber-500/30">
            Hook
          </Badge>
        )}
      </div>
      {news.details && (
        <p className="text-[11px] text-muted-foreground">{news.details}</p>
      )}
      <div className="flex items-center gap-2 text-[9px] text-muted-foreground/60">
        {news.source_name && <span>via {news.source_name}</span>}
        {news.reliability && (
          <span className={RELIABILITY_STYLES[news.reliability] || "text-muted-foreground"}>
            {news.reliability}
          </span>
        )}
        <span className="ml-auto">Turn {news.turn}</span>
      </div>
    </div>
  );
}